import React, { useCallback, useMemo } from 'react';
import { CloudUploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { Typography } from 'antd';

import { Button, Overlay, OverlayLoader } from 'components';

import useTheme from 'hooks/common/useTheme';

import { IFilesDropzoneProps } from './FilesDropzone.types';

import './FilesDropzone.styles.scss';

const { Text } = Typography;

const FilesDropzone = ({
  onChange,
  onError,
  multiple,
  loading,
  disabled,
  className = '',
  style,
  ...props
}: IFilesDropzoneProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: unknown[]) => {
      if (fileRejections.length) {
        return onError && onError(fileRejections);
      }

      if (acceptedFiles.length) {
        return onChange && onChange(acceptedFiles);
      }
    },
    [onError, onChange],
  );

  const { getRootProps, getInputProps, open, isFocused, isDragAccept, isDragReject } = useDropzone({
    noClick: true,
    noKeyboard: true,
    multiple,
    onDrop,
    disabled,
    ...props,
  });

  const actionClass = useMemo(() => {
    if (isFocused) {
      return 'np-files-dropzone--focused';
    }

    if (isDragAccept) {
      return 'np-files-dropzone--drag-accept';
    }

    if (isDragReject) {
      return 'np-files-dropzone--drag-reject';
    }
  }, [isDragAccept, isDragReject, isFocused]);

  return (
    <div {...getRootProps()} style={style} className={`np-files-dropzone ${actionClass} ${className}`}>
      <input {...getInputProps()} />

      <div className="np-files-dropzone__preview">
        <CloudUploadOutlined
          className={`np-files-dropzone__preview-icon ${
            (disabled || loading) && 'np-files-dropzone__preview-icon--disabled'
          }`}
        />

        <div className="np-files-dropzone__preview-text-wrapper">
          <Text>{t('common.dropFiles')}</Text>

          <div>
            <Text>{t('common.or')}</Text>

            <Button type="link" onClick={open} disabled={disabled || loading}>
              {t('form.button.browse')}
            </Button>
          </div>
        </div>
      </div>

      {loading && <OverlayLoader overlayColor={colors.MAIN_BG_COLOR.toString()} overlayOpacity={0.8} />}

      {disabled && <Overlay />}
    </div>
  );
};

export default FilesDropzone;
